/**
 * Bookmarks & Hooks (OutlierOS /library replica, docs/legacy-ui/01 §4) with tabs Bookmarked Reels · Saved
 * Profiles · Collections. Upgrades: bookmarks are actually stored, hooks follow the script rule, removed reels
 * show as unavailable.
 */
import { plural } from '@vw/core/src/plural';
import { useEffect, useMemo, useState } from 'react';
import { Bookmark, BookmarkX, Check, Copy, FolderHeart, Rocket, Users } from 'lucide-react';
import { EmptyState, Segmented, Spinner, TierBadge, copyText, formatWiseScore, toast, type TierThresholds } from '@vw/ui';
import { api, type BookmarkItem, type CollectionSummary, type SavedCreator } from '../api';
import { MetricsStrip } from '../components/CreatorGrid';
import { CreatorAvatar } from '../components/CreatorAvatar';
import { ago } from '../format';
import { Link, navigate, useLocation } from '../router';
import { useSession } from '../session';

export function Bookmarks() {
  const { query } = useLocation();
  const q = query.get('tab');
  const tab: 'reels' | 'profiles' | 'collections' = q === 'profiles' || q === 'collections' ? q : 'reels';
  const { library, settings } = useSession();
  const [marks, setMarks] = useState<BookmarkItem[] | null>(null);
  const [cols, setCols] = useState<CollectionSummary[] | null>(null);
  useEffect(() => {
    api<{ bookmarks: BookmarkItem[] }>('/api/bookmarks').then((r) => setMarks(r.bookmarks), () => setMarks([]));
    api<{ collections: CollectionSummary[] }>('/api/collections').then((r) => setCols(r.collections), () => setCols([]));
  }, []);
  const profiles = useMemo(() => (library ?? []).filter((c): c is SavedCreator => !c.unavailable), [library]);
  const setTabUrl = (t: typeof tab) => navigate(`/library${t === 'reels' ? '' : `?tab=${t}`}`, { replace: true });

  return (
    <div className="mx-auto max-w-[1400px] space-y-6">
      <section className="rounded-3xl border border-border bg-card p-6 shadow-sm sm:p-8">
        <h1 className="text-2xl font-extrabold tracking-tight sm:text-3xl">Bookmarks & Hooks</h1>
        <p className="mt-1 text-sm text-muted-foreground">Your swipe file of bookmarked viral reels, spoken hooks and reference creators.</p>
        <Segmented className="mt-5" value={tab} onChange={setTabUrl} options={[
          { value: 'reels', label: <><Bookmark className="h-3.5 w-3.5" />Bookmarked Reels ({marks?.length ?? '…'})</> },
          { value: 'profiles', label: <><Users className="h-3.5 w-3.5" />Saved Profiles ({profiles.length})</> },
          { value: 'collections', label: <><FolderHeart className="h-3.5 w-3.5" />Collections ({cols?.length ?? '…'})</> },
        ]} />
      </section>

      {tab === 'reels' && (!marks ? <Spinner label="Loading bookmarks…" /> : !marks.length ? (
        <EmptyState icon={<Bookmark className="h-7 w-7" />} title="No bookmarked reels yet"
          actions={<Link to="/explore" className="text-sm font-semibold text-primary">Browse the Creator Library →</Link>}>
          Open any creator's analysis, open a reel and press Bookmark to save its spoken hook here.
        </EmptyState>
      ) : (
        <div className="grid gap-5 md:grid-cols-2 lg:grid-cols-3">
          {marks.map((m) => <BookmarkCard key={m.reelPk} m={m} tiers={settings?.tiers ?? null} onRemoved={() => setMarks(marks.filter((x) => x.reelPk !== m.reelPk))} />)}
        </div>
      ))}

      {tab === 'profiles' && (!profiles.length ? (
        <EmptyState icon={<Users className="h-7 w-7" />} title="No saved profiles yet" actions={<Link to="/explore" className="text-sm font-semibold text-primary">Browse the Creator Library →</Link>}>
          Creators you scan or save appear here.
        </EmptyState>
      ) : (
        <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-4">
          {profiles.map((c) => (
            <article key={c.igPk} className="flex flex-col gap-3 rounded-2xl border border-border bg-card p-5 shadow-xs">
              <div className="flex items-center gap-3"><CreatorAvatar url={c.avatarUrl} name={c.handle ?? ''} fullName={c.fullName} size={40} />
                <div className="min-w-0"><div className="truncate text-sm font-bold">{c.fullName || `@${c.handle}`}</div><div className="text-xs text-muted-foreground">@{c.handle}</div></div></div>
              <MetricsStrip c={c} />
              <Link to={`/c/${c.handle}`} className="flex items-center justify-center gap-1.5 rounded-xl bg-primary px-3 py-2 text-xs font-semibold text-primary-foreground"><Rocket className="h-3.5 w-3.5" />Open Analysis</Link>
            </article>
          ))}
        </div>
      ))}

      {tab === 'collections' && (!cols ? <Spinner /> : (
        <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
          {cols.map((c) => (
            <Link key={c.id} to={`/collections/${c.id}`} className="flex gap-4 overflow-hidden rounded-2xl border border-border bg-card shadow-xs transition-shadow hover:shadow-md">
              {c.coverUrl ? <img src={c.coverUrl} alt="" referrerPolicy="no-referrer" className="h-24 w-16 object-cover" /> : <span className="flex h-24 w-16 items-center justify-center bg-secondary text-2xl">{c.emoji ?? '📁'}</span>}
              <span className="py-3 pr-3"><span className="block font-bold">{c.emoji ? `${c.emoji} ` : ''}{c.name}</span><span className="block text-xs text-muted-foreground">{plural(c.itemCount, 'reel')} · updated {ago(c.updatedAt)}</span></span>
            </Link>
          ))}
          <Link to="/collections" className="flex items-center justify-center rounded-2xl border border-dashed border-border p-6 text-sm font-semibold text-primary hover:bg-secondary/50">Open all collections →</Link>
        </div>
      ))}
    </div>
  );
}

function BookmarkCard({ m, tiers, onRemoved }: { m: BookmarkItem; tiers: TierThresholds | null; onRemoved: () => void }) {
  const [copied, setCopied] = useState(false);
  const r = m.reel;
  async function remove() {
    try {
      await api(`/api/bookmarks/${m.reelPk}`, { method: 'DELETE' });
      onRemoved();
    } catch (e) {
      toast.error('Could not remove the bookmark', { description: (e as Error).message });
    }
  }
  if (!r) {
    return (
      <article className="flex items-center justify-between gap-3 rounded-2xl border border-dashed border-border p-5 text-sm italic text-muted-foreground">
        This reel is no longer available.
        <button aria-label="Remove bookmark" onClick={remove} className="rounded-lg p-1.5 hover:bg-secondary"><BookmarkX className="h-4 w-4" /></button>
      </article>
    );
  }
  const hook = r.hook;
  return (
    <article className="flex flex-col gap-3 rounded-2xl border border-border bg-card p-5 shadow-xs">
      <div className="flex items-center justify-between gap-2">
        {tiers ? <TierBadge score={r.outlierScore} tiers={tiers} /> : r.outlierScore !== null && <span className="text-xs font-black text-amber-600">{formatWiseScore(r.outlierScore)}</span>}
        <span className="truncate text-xs font-semibold text-muted-foreground">@{r.ownerHandle}</span>
        <button aria-label="Remove bookmark" title="Remove bookmark" onClick={remove} className="rounded-lg p-1.5 text-muted-foreground hover:bg-secondary hover:text-rose-500"><BookmarkX className="h-4 w-4" /></button>
      </div>
      {hook && <blockquote lang={r.hookScript === 'devanagari' ? 'hi' : undefined} className="rounded-xl bg-secondary/70 p-3 text-sm font-semibold italic">“{hook}”</blockquote>}
      {!r.hook && <p className="text-[11px] text-muted-foreground">No spoken hook yet: transcribe the reel to get it.</p>}
      {r.caption && <p className="line-clamp-2 text-xs text-muted-foreground">{r.caption}</p>}
      {m.note && <p className="rounded-lg bg-primary/5 px-2 py-1 text-xs">{m.note}</p>}
      <div className="mt-auto flex items-center gap-2 border-t border-border pt-3">
        <button disabled={!r.hook} onClick={async () => { if (await copyText(r.hook!, { quiet: true })) { setCopied(true); setTimeout(() => setCopied(false), 2000); } }}
          className="inline-flex items-center gap-1.5 rounded-xl border border-border px-3 py-1.5 text-xs font-semibold hover:bg-secondary disabled:opacity-40">
          {copied ? <><Check className="h-3.5 w-3.5 text-emerald-500" />Copied!</> : <><Copy className="h-3.5 w-3.5" />Copy Hook</>}
        </button>
        {r.ownerHandle && <Link to={`/c/${r.ownerHandle}`} className="ml-auto text-xs font-semibold text-primary">Inspect Creator →</Link>}
      </div>
    </article>
  );
}
